import React from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { CheckCircle2, MessageCircle, ArrowRight, Loader2 } from "lucide-react";
import { buildWhatsAppLink } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const packages = [
  "Not sure yet",
  "7 days",
  "14 days",
  "21 days",
  "28 days",
  "42 days",
];

export const BookingForm = () => {
  const [form, setForm] = React.useState({
    name: "",
    whatsapp: "",
    due_date: "",
    package: "",
    message: "",
  });
  const [status, setStatus] = React.useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = React.useState("");

  const onChange = (e) =>
    setForm((s) => ({ ...s, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.whatsapp.trim()) {
      setErrorMsg("Please share your name and WhatsApp number.");
      setStatus("error");
      return;
    }
    setStatus("submitting");
    setErrorMsg("");
    try {
      await axios.post(`${API}/leads`, form);
      setStatus("success");
    } catch (err) {
      setErrorMsg(
        err?.response?.data?.detail ||
          "Something went wrong. Please try again or message us on WhatsApp."
      );
      setStatus("error");
    }
  };

  return (
    <section
      id="enquire"
      className="py-16 md:py-36 bg-ivory"
      data-testid="booking-section"
    >
      <div className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 flex flex-col justify-center"
          >
            <div className="text-xs uppercase tracking-[0.22em] text-sage font-medium">
              Reserve your dates
            </div>
            <h2 className="mt-5 font-serif text-3xl sm:text-4xl lg:text-5xl text-charcoal leading-tight tracking-tight">
              Begin a quiet <span className="italic text-sage-700">conversation</span>.
            </h2>
            <p className="mt-5 text-base md:text-lg text-charcoal/65 leading-relaxed">
              Share a few details and we&apos;ll get back personally, usually
              within an hour. No call centres, no forms inside forms.
            </p>

            <div className="mt-8 flex items-start gap-3 text-sm text-charcoal/70">
              <CheckCircle2 size={18} className="text-sage mt-0.5 shrink-0" />
              <span>Doctor-led intake, not a booking agent.</span>
            </div>
            <div className="mt-3 flex items-start gap-3 text-sm text-charcoal/70">
              <CheckCircle2 size={18} className="text-sage mt-0.5 shrink-0" />
              <span>Replies in English, Malayalam or Tamil.</span>
            </div>
            <div className="mt-3 flex items-start gap-3 text-sm text-charcoal/70">
              <CheckCircle2 size={18} className="text-sage mt-0.5 shrink-0" />
              <span>Your details remain private, used only to reach you.</span>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7"
          >
            <div className="bg-white border border-sage/15 rounded-3xl p-7 md:p-10">
              {status === "success" ? (
                <SuccessState onReset={() => {
                  setForm({ name: "", whatsapp: "", due_date: "", package: "", message: "" });
                  setStatus("idle");
                }} />
              ) : (
                <form onSubmit={onSubmit} className="space-y-5" data-testid="booking-form">
                  <Field
                    label="Your name"
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="e.g. Anjali Menon"
                    required
                    testid="form-name"
                  />
                  <div className="grid sm:grid-cols-2 gap-5">
                    <Field
                      label="WhatsApp number"
                      name="whatsapp"
                      value={form.whatsapp}
                      onChange={onChange}
                      placeholder="+91 9XXXXXXXXX"
                      required
                      testid="form-whatsapp"
                    />
                    <Field
                      label="Expected due date"
                      name="due_date"
                      value={form.due_date}
                      onChange={onChange}
                      placeholder="e.g. 20 Jan 2026"
                      testid="form-due-date"
                    />
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-[0.18em] text-sage font-medium mb-2">
                      Package you&apos;re considering
                    </label>
                    <select
                      name="package"
                      value={form.package}
                      onChange={onChange}
                      className="w-full bg-ivory border border-sage/25 rounded-xl px-4 py-3 text-charcoal text-sm focus:outline-none focus:border-sage transition-colors"
                      data-testid="form-package"
                    >
                      <option value="">Select a package</option>
                      {packages.map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs uppercase tracking-[0.18em] text-sage font-medium mb-2">
                      Anything you&apos;d like us to know
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      rows={4}
                      placeholder="C-section, dietary preferences, travelling family, anything at all..."
                      className="w-full bg-ivory border border-sage/25 rounded-xl px-4 py-3 text-charcoal text-sm focus:outline-none focus:border-sage transition-colors resize-none"
                      data-testid="form-message"
                    />
                  </div>

                  {status === "error" && errorMsg && (
                    <div
                      className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-xl px-4 py-3"
                      data-testid="form-error"
                    >
                      {errorMsg}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3 pt-2">
                    <button
                      type="submit"
                      disabled={status === "submitting"}
                      className="inline-flex items-center justify-center gap-2 bg-charcoal text-ivory px-7 py-3.5 rounded-full text-sm font-medium hover:bg-sage-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      data-testid="form-submit"
                    >
                      {status === "submitting" ? (
                        <>
                          <Loader2 size={16} className="animate-spin" />
                          Sending…
                        </>
                      ) : (
                        <>
                          Send enquiry
                          <ArrowRight size={15} />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, testid, ...props }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] text-sage font-medium mb-2">
      {label}
    </label>
    <input
      type="text"
      className="w-full bg-ivory border border-sage/25 rounded-xl px-4 py-3 text-charcoal text-sm focus:outline-none focus:border-sage transition-colors"
      data-testid={testid}
      {...props}
    />
  </div>
);

const SuccessState = ({ onReset }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
    className="text-center py-6"
    data-testid="form-success"
  >
    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sage/10 text-sage">
      <CheckCircle2 size={28} strokeWidth={1.5} />
    </div>
    <h3 className="mt-6 font-serif text-3xl text-charcoal">Thank you.</h3>
    <p className="mt-3 text-base text-charcoal/65 max-w-md mx-auto leading-relaxed">
      We&apos;ve received your enquiry and will reach out personally on
      WhatsApp shortly. If you&apos;d like, you can also start the
      conversation now.
    </p>
    <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
      <a
        href={buildWhatsAppLink("availability")}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center justify-center gap-2 bg-sage text-ivory px-6 py-3 rounded-full text-sm font-medium hover:bg-sage-700 transition-colors"
        data-testid="success-whatsapp"
      >
        <MessageCircle size={15} />
        Continue on WhatsApp
      </a>
      <button
        type="button"
        onClick={onReset}
        className="inline-flex items-center justify-center text-sm text-charcoal/60 hover:text-charcoal transition-colors"
        data-testid="success-reset"
      >
        Send another enquiry
      </button>
    </div>
  </motion.div>
);

export default BookingForm;
